// src/components/quiz/FindQuizzesView.tsx
import React, { useState, useEffect } from 'react';
import { Trash,Trash2, Edit, Eye,Globe,GlobeLock,Gift, Star, User, WifiOff, Share2,UserX} from 'lucide-react';
import { Quiz, QuizSummary } from '../../types'; // QuizSummary をインポート

// searchQuizzesInFirebase をインポート
import { searchQuizzesInFirebase, deleteQuiz } from '../../services/quizService';
import { useMyQuizSelection } from '../../hooks/useMyQuizSelection'
import { addMyQuizSelection, removeQuizfromMyQuizSelection, showQuiz } from '../../services/myQuizSelectionService';
import { logger } from '../../utils/logger';
import { useOnlineStatus } from '../../hooks/useOnlineStatus'; // 　オンライン判定用
import MyQuizIDShareComponent from './MyQuizIDShareComponent';
import QuizIcon from './QuizIcon';

import { useUserProfile } from '../../hooks/useUserProfile';


/** FindQuizzesView コンポーネントのProps型定義 */
interface FindQuizzesViewProps {
    userId: string | undefined;
    handlePageChange: (page: string) => void;
    showMessage: (message: string, onConfirm?: () => void, onCancel?: () => void, allowDismiss?: boolean) => void;
    startQuiz: (quizId: string) => void;
    handleEditQuizId: (quizId: string) => void;
}

// 検索数の最大件数（デフォルト値）
const MAX_SESTCH_COUNT = 16;



const FindQuizzesView: React.FC<FindQuizzesViewProps> = ({
    userId,
    handlePageChange,
    showMessage,
    startQuiz,
    handleEditQuizId,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<Quiz[]>([]);
    const [searching, setSearching] = useState(false);
    // 新しい状態を追加: 検索が実行されたかどうかを追跡
    const [hasSearched, setHasSearched] = useState(false);

    //ニックネームをとるために利用
    const { profile, isLoading: profileLoading, error: profileError } = useUserProfile(userId);

    // シェアモーダル用
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [quizToShare, setQuizToShare] = useState<{ id: string, title: string } | null>(null);

    // useMyQuizSelection フックを使用してクイズデータをフェッチ
    const { myQuizSelection, isLoading: myQuizLoading, isError: myQuizError, mutate: myQuizMute } = useMyQuizSelection(userId || '');

    // 非表示のクイズIDリストに基づいて表示するクイズサマリーのState
    const [hiddenQuizSummariesForDisplay, setHiddenQuizSummariesForDisplay] = useState<QuizSummary[]>([]);

    const isOnline = useOnlineStatus();

    // MOD: プロフィール情報に基づいて検索件数を決定
    const searchLimit = profile?.searchQuizLimit || MAX_SESTCH_COUNT;

    // myQuizSelection の変更を監視し、非表示クイズの表示リストを更新
    useEffect(() => {
        if (myQuizSelection && myQuizSelection.hiddenQuizIdList && myQuizSelection.quizSummaries) {
            // hiddenQuizIdList に含まれるIDを持つクイズサマリーをフィルタリング
            const filteredSummaries = myQuizSelection.quizSummaries.filter(summary =>
                myQuizSelection.hiddenQuizIdList.includes(summary.id)
            );
            setHiddenQuizSummariesForDisplay(filteredSummaries);
        } else {
            setHiddenQuizSummariesForDisplay([]); // データがない場合は空にする
        }
    }, [myQuizSelection]); // myQuizSelection が変更されたときにこのエフェクトを再実行


    /**
     * クイズ検索ハンドラ
     */
    const handleSearch = async () => {
        if (!isOnline) {
            showMessage("オフラインのため、クイズを検索できません。");
            return;
        }
        setSearching(true);
        try {
            // MOD: 固定値ではなく、動的に決定した searchLimit を使用する
            const results = await searchQuizzesInFirebase(searchTerm, searchLimit);
            setSearchResults(results);
        } catch (error) {
            logger.error("クイズ検索に失敗しました:", error);
            showMessage("クイズの検索中にエラーが発生しました。");
        } finally {
            setSearching(false);
            // 検索が完了したことをマーク
            setHasSearched(true);
        }
    };

    /**
    * クイズをコレクションに追加するハンドラ
     */
    const handleAddQuiz = async (quiz: Quiz) => {
        if (!isOnline) {
            showMessage("オフラインのため、クイズを追加できません。");
            return;
        }
        if (!userId) {
            showMessage('ログインしてください。');
            return;
        }
        await addMyQuizSelection(userId, quiz);
        myQuizMute();
    };

    /**
    * クイズを再表示するハンドラ
     */
    const handleShowQuiz = async (quizId: string) => {
        if (!isOnline) {
            showMessage("オフラインのため、クイズを再表示できません。");
            return;
        }
        if (!userId) {
            showMessage('ログインしてください。');
            return;
        }
        await showQuiz(userId, quizId);
        myQuizMute();
    };

    /**
    * クイズをリストから削除するハンドラ
     */
    const handleRemoveFromMyQuizSelection = async (quizId: string) => {
        if (!isOnline) {
            showMessage("オフラインのため、クイズを削除できません。");
            return;
        }
        if (!userId) {
            showMessage('ログインしてください。');
            return;
        }
        await removeQuizfromMyQuizSelection(userId, quizId);
        myQuizMute();
    };

    /**
    // クイズ削除確認ハンドラ（自分が作成したクイズ用）
     */
    const handleDeleteQuiz = (quizId: string) => {
        if (!isOnline) {
            showMessage("オフラインのため、クイズを削除できません。");
            return;
        }
        showMessage(
            '自分で作成したクイズを丸ごと削除します。一度削除すると元に戻せません。本当にこのクイズを削除しますか？',
            async () => { // ここを async に変更
                try {
                    await deleteQuiz(quizId); // await を追加
                    // クイズ削除が成功した場合のみ、MyQuizSelection からも削除
                    await removeQuizfromMyQuizSelection(userId, quizId); // await を追加
                    myQuizMute(); // 全ての操作完了後にデータを再フェッチ
                } catch (error) {
                    logger.error("クイズの削除に失敗しました:", error);
                    showMessage("クイズの削除中にエラーが発生しました。");
                }
            },
            undefined,
            true
        );
    };

    // 検索結果に表示されているクイズがMyQuizSelectionに追加済みかどうかを判定するヘルパー関数
    const isQuizAddedToMySelection = (quizId: string): boolean => {
        return myQuizSelection?.quizSummaries?.some(summary => summary.id === quizId) || false;
    };


    return (
        <React.Fragment>
            <div className="container mx-auto p-4 max-w-2xl bg-white shadow-lg rounded-lg min-h-[calc(100vh-80px)]">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">クイズを探す・管理する</h2>

                {!isOnline && (
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded-md flex items-center" role="alert">
                        <WifiOff size={24} className="mr-3"/>
                        <div>
                            <p className="font-bold">現在オフラインです</p>
                            <p>インターネット接続が回復するまで、一部の機能は利用できません。</p>
                        </div>
                    </div>
                )}

                {/* クイズ検索セクション */}
                <div className="mb-8 p-4 border rounded-lg bg-gray-50">
                    <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center justify-center">
                        <Globe color="#2ECC71" size={20} aria-label="公開クイズ" />公開クイズを検索
                    </h3>
                    <div className="flex items-center space-x-2">
                        <input
                            type="text"
                            placeholder="作成者、カテゴリ、クイズID(ID_で始まる)で検索"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                            disabled={!isOnline}
                            className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-gray-200"
                        />
                        <button
                            onClick={handleSearch}
                            disabled={searching || !isOnline}
                            className="py-3 px-6 bg-sky-500 text-white font-bold rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {searching ? '検索中...' : '検索'}
                        </button>
                    </div>
                    {searchResults.length > 0 && (
                        <div className="mt-6">
                            {/* MOD: 最大検索件数の表示を動的に変更 */}
                            <h4 className="text-lg font-medium text-gray-700 mb-3">検索結果: (Maximum {searchLimit} search)</h4>
                            <ul className="space-y-3">
                                {searchResults.map((quiz) => (
                                    <li key={quiz.id} className="p-4 border border-gray-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white space-y-4 sm:space-y-0">
                                        {/* MOD: text-left を追加して左寄せを保証 */}
                                        <div className="flex-1 min-w-0 text-left">
                                            <p className="font-semibold text-gray-900 break-words">{quiz.title}</p>
                                            <p className="text-sm text-gray-600">作成者: {quiz.authorNickname || '不明'}</p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {Array.isArray(quiz.category) ? (
                                                    quiz.category.map((cat, index) => (
                                                        <span
                                                            key={index}
                                                            className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium"
                                                        >
                                                            {cat}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium">
                                                        {quiz.category}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-end sm:justify-start space-x-2 flex-shrink-0 sm:ml-4">
                                            {isQuizAddedToMySelection(quiz.id) ? (
                                                <span className="text-green-500 text-sm px-4">追加済み</span>
                                            ) : (
                                                <button onClick={() => handleAddQuiz(quiz)} disabled={!isOnline} className="py-2 px-4 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed">
                                                    追加
                                                </button>
                                            )}
                                            <button onClick={() => startQuiz(quiz.id!)} disabled={!isOnline} className="py-2 px-4 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed">
                                                開始
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {/* 検索が実行され、かつ結果がない場合にのみメッセージを表示 */}
                    {hasSearched && searchResults.length === 0 && (
                        <p className="text-center text-gray-500 mt-4">該当するクイズは見つかりませんでした。</p>
                    )}
                </div>

                {/* 非表示のクイズセクション */}
                <div className="mb-8 p-4 border rounded-lg bg-gray-50">
                    <h3 className="text-xl font-semibold text-gray-700 mb-4">非表示のクイズ</h3>
                    {hiddenQuizSummariesForDisplay.length > 0 ? (
                        <ul className="space-y-3">
                            {hiddenQuizSummariesForDisplay.map((quizSummary) => (
                                <li key={quizSummary.id} className="p-4 border border-gray-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white space-y-4 sm:space-y-0">
                                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                                        {/* QuizIconコンポーネントを使用 */}
                                        <div className="flex-shrink-0 pt-1">
                                            <QuizIcon quizSummary={quizSummary} userId={userId} />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <p className="font-semibold text-gray-900 break-words">{quizSummary.title}</p>
                                            <p className="text-sm text-gray-600">作成者: {quizSummary.authorNickname || '不明'}</p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {Array.isArray(quizSummary.category) ? (
                                                    quizSummary.category.map((cat, index) => (
                                                        <span
                                                            key={index}
                                                            className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium"
                                                        >
                                                            {cat}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium">
                                                        {quizSummary.category}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end sm:justify-center space-x-2 w-full sm:w-auto flex-shrink-0 sm:ml-4">
                                        {quizSummary.authorId === userId ? (
                                            <>
                                                <button onClick={() => handleEditQuizId(quizSummary.id)} disabled={!isOnline} className="p-2 text-blue-500 hover:bg-blue-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
                                                    <Edit size={18} />
                                                </button>
                                                
                                                {/* 公開クイズの場合にのみシェアボタンを表示 */}
                                                {quizSummary.isPublic && (
                                                <button
                                                    onClick={() => {
                                                        setIsShareModalOpen(true);
                                                        setQuizToShare({ id: quizSummary.id, title: quizSummary.title });
                                                    }}
                                                    disabled={!isOnline}
                                                    className="p-2 text-sky-500 hover:bg-sky-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <Share2 size={18} />
                                                </button>
                                                )}

                                                <button onClick={() => handleDeleteQuiz(quizSummary.id)} disabled={!isOnline} className="p-2 text-red-500 hover:bg-red-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
                                                    <Trash2 size={18} />
                                                </button>
                                            </>
                                        ) : quizSummary.isGiftedItem === true ? (
                                            <p/>
                                        ) :
                                        (
                                            <button onClick={() => { showMessage('このクイズを非表示リストからも削除しますか？再度検索して見つけることができます。', () => handleRemoveFromMyQuizSelection(quizSummary.id)) }} disabled={!isOnline} className="p-2 text-red-500 hover:bg-red-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
                                                <Trash size={18} />
                                            </button>
                                        )}
                                        <button onClick={() => handleShowQuiz(quizSummary.id)} disabled={!isOnline} className="flex items-center py-2 px-3 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
                                            <Eye size={16} className="mr-2" /> 再表示
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-center text-gray-500 py-4">非表示のクイズはありません。</p>
                    )}
                </div>
            </div>
            {isShareModalOpen && quizToShare && (
                <MyQuizIDShareComponent
                    quizId={quizToShare.id}
                    quizTitle={quizToShare.title}
                    onClose={() => setIsShareModalOpen(false)}
                />
            )}
        </React.Fragment>
    );
};

export default FindQuizzesView;